import React, { useState, useCallback, useMemo, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import Checkbox from "../../../Checkbox"; 

const getInitials = (name) => {
  if (!name) return "NA";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const PortalTooltip = ({ children, tooltipContent, align = "center", triggerClassName = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (triggerRef.current && tooltipRef.current && isVisible) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      let top = triggerRect.top - tooltipRect.height - 12; 
      let left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);

      const padding = 10;
      if (left < padding) {
        left = padding;
      } else if (left + tooltipRect.width > window.innerWidth - padding) {
        left = window.innerWidth - tooltipRect.width - padding;
      }

      setCoords({ top, left });
    }
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      updatePosition();
      
      let ticking = false;
      const onScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            updatePosition();
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener("scroll", onScroll, true);
      window.addEventListener("resize", onScroll);
      
      return () => {
        window.removeEventListener("scroll", onScroll, true);
        window.removeEventListener("resize", onScroll);
      };
    }
  }, [isVisible, updatePosition]);

  return (
    <>
      <div 
        ref={triggerRef}
        className={triggerClassName}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>

      {isVisible && tooltipContent && createPortal(
        <div
          ref={tooltipRef}
          className="fixed z-[9999] pointer-events-none animate-fadeIn"
          style={{
            top: coords.top,
            left: coords.left,
            opacity: coords.top ? 1 : 0 
          }}
        >
          <div className={`bg-white rounded-[10px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-100 p-2 relative w-max min-w-[120px] max-w-[400px] ${align === "center" ? "text-center" : "text-left"}`}>
            <div className="font-outfit text-[#171717] text-[14px] whitespace-normal break-words font-normal">
              {tooltipContent}
            </div>
            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-white"></div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

const TruncatedTextWithTooltip = ({ text }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    if (textRef.current) {
      const { scrollHeight, clientHeight, scrollWidth, clientWidth } = textRef.current;
      setIsTruncated(scrollHeight > clientHeight || scrollWidth > clientWidth);
    }
  }, []);

  useLayoutEffect(() => {
    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [checkTruncation, text]);

  const TextElement = (
    <div ref={textRef} className="line-clamp-2 w-full break-words">
      {text}
    </div>
  );

  if (isTruncated) {
    return (
      <PortalTooltip 
        tooltipContent={text}
        align="left"
        triggerClassName="w-full block cursor-pointer"
      >
        {TextElement}
      </PortalTooltip>
    );
  }
  return TextElement;
};

const FeedbackPill = ({ type }) => {
  const isGood = type === "Good" || type === "up";
  const baseClasses = "inline-flex items-center gap-1.5 px-3 py-[2px] rounded-full text-[13px] font-medium border font-outfit capitalize bg-white";
  
  if (isGood) {
    return (
      <span className={`${baseClasses} border-[#198808] text-[#198808]`}>
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
        </svg>
        Good
      </span>
    );
  }
  return (
    <span className={`${baseClasses} border-[#E21919BF] text-[#E74746]`}>
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
      </svg>
      Bad
    </span>
  );
};

const DynamicAnalyticsDataRow = React.memo(({
  item,
  isSelected,
  onToggleSelect,
  activeColumns,
  columnDefs,
}) => {
  const rowActions = { isSelected, onToggleSelect };
  return (
    <tr className="bg-white hover:bg-[#F5F5F5] transition-colors cursor-default group border-b border-[#0000001A]">
      {activeColumns.map((col) => columnDefs[col.name]?.cell(item, rowActions))}
    </tr>
  );
});

const ResponseEvalDataTable = ({
  data = [],
  loading = false,
  selectedRows = [],
  onSelectAll,
  onSelectRow,
  columnPreferences, 
  onViewResponse, 
}) => {
  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const savedWidths = localStorage.getItem("analyticsTable_columnWidths_v1");
      return savedWidths ? JSON.parse(savedWidths) : {};
    } catch (error) {
      return {};
    }
  });

  const handleResize = useCallback((colName, e) => {
    e.preventDefault();
    e.stopPropagation();

    const thElement = e.target.closest("th");
    const startWidth = thElement ? thElement.getBoundingClientRect().width : 150;
    const startX = e.clientX;

    const onMouseMove = (moveEvent) => {
      const minW = colName === "user_query" ? 250 : 100;
      const newWidth = Math.max(minW, startWidth + (moveEvent.clientX - startX));
      setColumnWidths((prev) => ({ ...prev, [colName]: newWidth }));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "default";

      setColumnWidths((latestWidths) => {
        localStorage.setItem("analyticsTable_columnWidths_v1", JSON.stringify(latestWidths));
        return latestWidths;
      });
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "col-resize";
  }, []);

  const getColStyle = useCallback((colName) => {
    return columnWidths[colName]
      ? { width: columnWidths[colName], minWidth: columnWidths[colName], maxWidth: columnWidths[colName] }
      : {};
  }, [columnWidths]);

  const isAllChecked = data.length > 0 && selectedRows.length === data.length;

  const defaultCols = [
    { name: "timestamp", order: 0, visible: true },
    { name: "feedback", order: 1, visible: true },
    { name: "user_query", order: 2, visible: true },
    { name: "response", order: 3, visible: true },
    { name: "context", order: 4, visible: true },
    { name: "member", order: 5, visible: true },
    { name: "feedback_comment", order: 6, visible: true },
  ];

  const activeColumns = useMemo(() => {
    return (columnPreferences && columnPreferences.length > 0 ? columnPreferences : defaultCols)
      .filter((col) => col.visible === true || col.visible === "true")
      .sort((a, b) => a.order - b.order);
  }, [columnPreferences]);

  const visibleColCount = activeColumns.length;

  const columnDefs = useMemo(() => ({
    timestamp: {
      header: (
        <th
          key="timestamp"
          style={getColStyle("timestamp")}
          className={`${!columnWidths.timestamp ? "w-[220px] min-w-[220px]" : ""} sticky top-0 z-[50] relative px-3 py-2 text-left text-[#00000080] font-outfit text-[14px] font-semibold border-r border-[#0000001A] bg-[#F7F7F7] shadow-[0_1px_0_rgba(0,0,0,0.1)]`}
        >
          <div className="flex items-center gap-3 w-full h-full">
            <div className="shrink-0 w-[24px]">
              <Checkbox checked={isAllChecked} onChange={onSelectAll} disabled={data.length === 0} showLabel={false} size="md" />
            </div>
            <span className="truncate block pr-2">Date & Time</span>
          </div>
          <div onMouseDown={(e) => handleResize("timestamp", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#C95EBE] transition-colors z-[60]" />
        </th>
      ),
      cell: (item, rowActions) => (
        <td
          key={`timestamp-${item.id}`}
          style={getColStyle("timestamp")}
          className={`${!columnWidths.timestamp ? "w-[220px] min-w-[220px]" : ""} px-3 py-3 text-left text-[#52525B] font-outfit text-[14px] font-medium border-b border-r border-[#0000001A] bg-transparent`}
        >
          <div className="flex items-center gap-3 w-full h-full min-w-0">
            <div className="shrink-0 w-[24px]" onClick={(e) => e.stopPropagation()}>
              <Checkbox id={`checkbox-${item.id}`} checked={rowActions.isSelected} onChange={(val, e) => { if (e) e.stopPropagation(); rowActions.onToggleSelect(item.id); }} disabled={!item.id} showLabel={false} size="md" />
            </div>
            <span className="truncate block pr-2" title={item.timestamp}>{item.timestamp || "_"}</span>
          </div>
        </td>
      ),
    },
    feedback: {
      header: (
        <th 
          key="feedback" 
          style={getColStyle("feedback")} 
          className={`${!columnWidths.feedback ? "w-[120px] min-w-[120px]" : ""} sticky top-0 z-[50] relative px-3 text-left text-[#00000080] font-outfit text-[14px] font-semibold border-r border-[#0000001A] bg-[#F7F7F7] shadow-[0_1px_0_rgba(0,0,0,0.1)]`}
        >
          Feedback
          <div onMouseDown={(e) => handleResize("feedback", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#C95EBE] transition-colors z-[60]" />
        </th>
      ),
      cell: (item) => (
        <td key={`feedback-${item.id}`} style={getColStyle("feedback")} className={`${!columnWidths.feedback ? "w-[120px] min-w-[120px]" : ""} px-3 py-3 border-b border-r border-[#0000001A] bg-transparent text-left`}>
          <FeedbackPill type={item.feedback} />
        </td>
      ),
    },
    user_query: {
      header: (
        <th 
          key="user_query" 
          style={getColStyle("user_query")} 
          className={`${!columnWidths.user_query ? "w-[30%] min-w-[250px]" : ""} sticky top-0 z-[50] relative px-3 text-left text-[#00000080] font-outfit text-[14px] font-semibold border-r border-[#0000001A] bg-[#F7F7F7] shadow-[0_1px_0_rgba(0,0,0,0.1)]`}
        >
          User Query
          <div onMouseDown={(e) => handleResize("user_query", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#C95EBE] transition-colors z-[60]" />
        </th>
      ),
      cell: (item) => {
        return (
          <td key={`user_query-${item.id}`} style={getColStyle("user_query")} className={`${!columnWidths.user_query ? "w-[30%] min-w-[250px]" : ""} px-3 py-3 text-[#52525B] font-outfit text-[14px] font-medium border-b border-r border-[#0000001A] bg-transparent text-left`}>
            <TruncatedTextWithTooltip text={item.user_query} />
          </td>
        );
      },
    },
    response: {
      header: (
        <th 
          key="response" 
          style={getColStyle("response")} 
          className={`${!columnWidths.response ? "w-[140px] min-w-[140px]" : ""} sticky top-0 z-[50] relative px-3 text-left text-[#00000080] font-outfit text-[14px] font-semibold border-r border-[#0000001A] bg-[#F7F7F7] shadow-[0_1px_0_rgba(0,0,0,0.1)]`}
        >
          AI Response
          <div onMouseDown={(e) => handleResize("response", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#C95EBE] transition-colors z-[60]" />
        </th>
      ),
      cell: (item) => (
        <td key={`response-${item.id}`} style={getColStyle("response")} className={`${!columnWidths.response ? "w-[140px] min-w-[140px]" : ""} px-3 py-3 text-left text-[#52525B] font-outfit text-[14px] font-medium truncate border-b border-r border-[#0000001A] bg-transparent`}>
          <span 
            className="text-[#C95EBE] font-outfit text-[14px] font-medium hover:underline whitespace-nowrap cursor-pointer"
            onClick={() => onViewResponse(item)}
          >
            View Response
          </span>
        </td>
      ),
    },
    context: {
      header: (
        <th 
          key="context" 
          style={getColStyle("context")} 
          className={`${!columnWidths.context ? "w-[220px] min-w-[220px]" : ""} sticky top-0 z-[50] relative px-3 text-left text-[#00000080] font-outfit text-[14px] font-semibold border-r border-[#0000001A] bg-[#F7F7F7] shadow-[0_1px_0_rgba(0,0,0,0.1)]`}
        >
          Context
          <div onMouseDown={(e) => handleResize("context", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#C95EBE] transition-colors z-[60]" />
        </th>
      ),
      cell: (item) => (
        <td 
          key={`context-${item.id}`} 
          style={getColStyle("context")} 
          className={`${!columnWidths.context ? "w-[220px] min-w-[220px]" : ""} px-3 py-3 align-middle border-b border-r border-[#0000001A] bg-transparent min-w-0 text-left`}
        >
          <span 
            className="px-3 py-1 border border-[#DEDEDE] rounded-full text-[#52525B] text-[13px] font-medium font-outfit bg-white inline-block max-w-full truncate align-middle"
            title={item.context}
          >
            {item.context}
          </span>
        </td>
      ),
    },
    member: {
      header: (
        <th 
          key="member" 
          style={getColStyle("member")} 
          className={`${!columnWidths.member ? "w-[80px] min-w-[80px]" : ""} sticky top-0 z-[50] relative px-3 text-center text-[#00000080] font-outfit text-[14px] font-semibold border-r border-[#0000001A] bg-[#F7F7F7] shadow-[0_1px_0_rgba(0,0,0,0.1)]`}
        >
          User
          <div onMouseDown={(e) => handleResize("member", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#C95EBE] transition-colors z-[60]" />
        </th>
      ),
      cell: (item) => {
        const userTooltipContent = (
          <div className="flex flex-col items-center gap-0.5">
            <span className="font-outfit text-[#171717] font-semibold text-[14px]">
              {item.member}
            </span>
          </div>
        );

        return (
          <td 
            key={`member-${item.id}`} 
            style={getColStyle("member")} 
            className={`${!columnWidths.member ? "w-[80px] min-w-[80px]" : ""} px-2 py-1 align-middle border-b border-r border-[#0000001A] bg-transparent text-center`}
          >
            <PortalTooltip 
              tooltipContent={userTooltipContent}
              triggerClassName="relative inline-flex justify-center items-center group cursor-pointer"
            >
              <div className="w-9 h-9 rounded-full bg-[#C95EBE] flex items-center justify-center text-white font-outfit text-[13px] font-medium tracking-wide transition-transform group-hover:scale-105 shadow-[0_2px_10px_rgba(0,0,0,0.08)]">
                {getInitials(item.member)}
              </div>
            </PortalTooltip>
          </td>
        );
      }
    },
    feedback_comment: {
      header: (
        <th 
          key="feedback_comment" 
          style={getColStyle("feedback_comment")} 
          className={`${!columnWidths.feedback_comment ? "w-[250px] min-w-[200px]" : ""} sticky top-0 z-[50] relative px-3 text-left text-[#00000080] font-outfit text-[14px] font-semibold border-[#0000001A] bg-[#F7F7F7] shadow-[0_1px_0_rgba(0,0,0,0.1)]`}
        >
          Comment
          <div onMouseDown={(e) => handleResize("feedback_comment", e)} className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#C95EBE] transition-colors z-[60]" />
        </th>
      ),
      cell: (item) => {
        return (
          <td key={`feedback_comment-${item.id}`} style={getColStyle("feedback_comment")} className={`${!columnWidths.feedback_comment ? "w-[250px] min-w-[200px]" : ""} px-3 py-3 text-left text-[#52525B] font-outfit text-[14px] font-medium border-b border-[#0000001A] bg-transparent`}>
            <TruncatedTextWithTooltip text={item.feedback_comment} />
          </td>
        );
      },
    },
  }), [columnWidths, getColStyle, handleResize, isAllChecked, onSelectAll, data.length, onViewResponse]);

  return (
    <div className="w-full h-full overflow-auto custom-scrollbar relative">
      <table className="w-full min-w-[1200px] table-fixed text-sm text-left border-separate border-spacing-0 bg-white">
        <thead className="bg-[#F7F7F7] relative z-[50]">
          <tr>
            {activeColumns.map((col) => columnDefs[col.name]?.header)}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={visibleColCount} className="py-24 text-center align-middle border-b border-[#0000001A] bg-transparent relative">
                <div className="flex flex-col items-center justify-center w-full h-full min-h-[200px]">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C95EBE] mx-auto mb-4"></div>
                  <p className="text-[#52525B] font-outfit text-[15px]">Loading table data...</p>
                </div>
              </td>
            </tr>
          ) : data.length > 0 ? (
            data.map((item) => (
              <DynamicAnalyticsDataRow
                key={item.id}
                item={item}
                isSelected={selectedRows.includes(item.id)}
                onToggleSelect={onSelectRow}
                activeColumns={activeColumns}
                columnDefs={columnDefs}
              />
            ))
          ) : (
            <tr>
              <td colSpan={visibleColCount} className="py-10 text-center text-black border-b border-[#0000001A]">
                <img src="/no-contracts.svg" alt="No Data" className="mx-auto mb-2" />
                <p className="text-[16px] font-outfit">Looks like your table is empty!</p>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ResponseEvalDataTable;